import dayjs, { type Dayjs } from 'dayjs';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import {
  buildSuiteCounts,
  type SuiteStatusKey,
} from '@/features/dashboard/components/dashboardStatusDataHelpers';
import { formatRangeWindowNoDataText } from '@/features/dashboard/hooks';
import { querySuiteExecutions, querySuiteExecutionStatusSummary } from '@/features/executions/data';
import { EntityTrendsPageShell } from '@/features/shared/components/EntityTrendsPageShell';
import { CLONE_TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { TableToolbar } from '@/features/shared/components/tableSystem/TableToolbar';
import {
  resolveMetaDateBounds,
  type TableQueryMeta,
} from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import { useCloneDateWindow } from '@/features/shared/hooks/useCloneDateWindow';
import { useEntityTrendsPage } from '@/features/shared/hooks/useEntityTrendsPage';
import { SuiteExecutionsTable } from '@/features/suites/components/SuiteExecutionsTable';
import { SuitesTable } from '@/features/suites/components/SuitesTable';
import { querySuites, querySuiteSummary } from '@/features/suites/data/suites';
import {
  cloneExecutionColumnOptions,
  cloneExecutionDefaultColumnVisibilityModel,
  cloneExecutionDefaultSortDirection,
  cloneExecutionDefaultSortKey,
  type CloneExecutionSortDirection,
  cloneExecutionSortFieldMap,
  type CloneExecutionSortKey,
  cloneExecutionSortOptions,
  cloneExecutionStatusOptions,
} from '@/features/suites/pages/suiteExecutionsTableDefinition';
import {
  cloneColumnOptions,
  cloneDefaultColumnVisibilityModel,
  cloneDefaultSortDirection,
  cloneDefaultSortKey,
  type CloneSortDirection,
  cloneSortFieldMap,
  type CloneSortKey,
  cloneSortOptions,
  cloneStatusOptions,
} from '@/features/suites/pages/suitesTableDefinition';
import { suiteExecutionSummaryStatusOptions } from '@/shared/constants/statusOptions';
import { gridPaths, queryKeys, useResolvedFeatureFlags, useTagOptions } from '@/shared/hooks';
import { Stack } from '@/ui';
import {
  buildDateRangeFilters,
  buildEqFilterValue,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { Panel } from '@/ui/composites/Panel';
import { Box, type GridColumnVisibilityModel, Tab, Tabs } from '@/ui/primitives';
import type { CloneDateQuickFilter } from '@/features/shared/hooks/useCloneDateWindow';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  QueryResult,
  SuiteExecutionDto,
  SuiteExecutionStatus,
  SuiteExecutionStatusSummaryDto,
  SuiteListItemDto,
  SuiteSummaryDto,
} from '@flowqa/shared';

type CloneSuitesPanelTab = 'suites' | 'executions';

export const SuitesPage = (): ReactElement => {
  const { flags: featureFlags } = useResolvedFeatureFlags();
  const cloneTableEnabled = featureFlags?.suitesCloneTable ?? false;
  const viewModel = useEntityTrendsPage<
    SuiteListItemDto,
    SuiteSummaryDto | SuiteExecutionStatusSummaryDto
  >({
    loadingMessage: 'Loading suites...',
    metricsScope: 'suite',
    gridPath: gridPaths.suites,
    executionGridPath: gridPaths.suiteExecutions,
    entitySearchField: 'name',
    executionSearchField: 'suiteName',
    statusOptions: suiteExecutionSummaryStatusOptions.map((option) => ({
      value: option.value as Exclude<SuiteStatusKey, 'untested'>,
      label: option.label,
    })),
    summaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.suiteSummary(projectId, organisationId, filtersKey),
    executionSummaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.suiteExecutionSummary(projectId, organisationId, filtersKey),
    summaryPrefixKey: queryKeys.suiteSummaryPrefix,
    executionSummaryPrefixKey: queryKeys.suiteExecutionSummaryPrefix,
    queryLatestSummary: querySuiteSummary,
    queryExecutionSummary: querySuiteExecutionStatusSummary,
    buildCounts: buildSuiteCounts,
    selectThresholds: (thresholds) => thresholds.suites,
    queryEntityTable: async (
      input: GridQueryInput,
      {
        selectedProjectId,
        selectedOrganisationId,
        rangeStartIso,
        rangeEndIso,
        previousRangeStartIso,
        previousRangeEndIso,
        deltasEnabled,
      },
    ): Promise<QueryResult<SuiteListItemDto>> =>
      querySuites(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        metricsStartDay: rangeStartIso ?? undefined,
        metricsEndDay: rangeEndIso ?? undefined,
        previousMetricsStartDay: previousRangeStartIso ?? undefined,
        previousMetricsEndDay: previousRangeEndIso ?? undefined,
        include: {
          project: true,
          latestExecution: true,
          executionStatusCounts: true,
          latestTestResults: { counts: true },
          metrics: true,
          metricDeltas: deltasEnabled && previousRangeStartIso !== null,
        },
      }),
  });
  const { tagOptions } = useTagOptions({ projectId: viewModel.selectedProjectId });
  const initialCloneColumnVisibilityModel: GridColumnVisibilityModel = {
    ...cloneDefaultColumnVisibilityModel,
  };
  const initialCloneExecutionColumnVisibilityModel: GridColumnVisibilityModel = {
    ...cloneExecutionDefaultColumnVisibilityModel,
  };

  const [cloneDateQuickFilter, setCloneDateQuickFilter] = useState<CloneDateQuickFilter>('7d');
  const [cloneFromDate, setCloneFromDate] = useState<Dayjs | null>(null);
  const [cloneToDate, setCloneToDate] = useState<Dayjs | null>(null);
  const [debouncedCloneSearch, setDebouncedCloneSearch] = useState('');
  const [cloneColumnVisibilityModel, setCloneColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(initialCloneColumnVisibilityModel);
  const [cloneSortKey, setCloneSortKey] = useState<CloneSortKey>(cloneDefaultSortKey);
  const [cloneSortDirection, setCloneSortDirection] =
    useState<CloneSortDirection>(cloneDefaultSortDirection);
  const [cloneFilterStatuses, setCloneFilterStatuses] = useState<SuiteExecutionStatus[]>([]);
  const [cloneFilterTags, setCloneFilterTags] = useState<string[]>([]);
  const [cloneActiveTab, setCloneActiveTab] = useState<CloneSuitesPanelTab>('suites');
  const [debouncedCloneExecutionSearch, setDebouncedCloneExecutionSearch] = useState('');
  const [cloneExecutionColumnVisibilityModel, setCloneExecutionColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(initialCloneExecutionColumnVisibilityModel);
  const [cloneExecutionSortKey, setCloneExecutionSortKey] = useState<CloneExecutionSortKey>(
    cloneExecutionDefaultSortKey,
  );
  const [cloneExecutionSortDirection, setCloneExecutionSortDirection] =
    useState<CloneExecutionSortDirection>(cloneExecutionDefaultSortDirection);
  const [cloneExecutionFilterStatuses, setCloneExecutionFilterStatuses] = useState<
    SuiteExecutionStatus[]
  >([]);
  const [cloneExecutionFilterTags, setCloneExecutionFilterTags] = useState<string[]>([]);
  const [cloneSuitesMeta, setCloneSuitesMeta] = useState<TableQueryMeta<SuiteListItemDto> | null>(
    null,
  );
  const [cloneSuiteExecutionsMeta, setCloneSuiteExecutionsMeta] =
    useState<TableQueryMeta<SuiteExecutionDto> | null>(null);

  const cloneAvailableTags = useMemo(
    () =>
      Array.from(new Set(tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [tagOptions],
  );
  const handleDebouncedCloneSearchChange = useCallback((value: string) => {
    setDebouncedCloneSearch(value);
  }, []);
  const cloneExecutionAvailableTags = useMemo(
    () =>
      Array.from(new Set(tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [tagOptions],
  );
  const handleDebouncedCloneExecutionSearchChange = useCallback((value: string) => {
    setDebouncedCloneExecutionSearch(value);
  }, []);
  const showCloneDeltas = viewModel.deltasEnabled;
  const cloneDateWindow = useCloneDateWindow({
    dateQuickFilter: cloneDateQuickFilter,
    fromDate: cloneFromDate,
    toDate: cloneToDate,
  });
  const cloneQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<SuiteListItemDto>> => {
      const toolbarFilters = mergeFilters(
        buildInFilter('latestExecutionStatus', cloneFilterStatuses),
        buildInFilter('tags', cloneFilterTags),
      );
      const sortOverride: ApiSort = {
        field: cloneSortFieldMap[cloneSortKey],
        direction: cloneSortDirection,
      };
      return querySuites(
        viewModel.selectedProjectId,
        viewModel.selectedOrganisationId ?? undefined,
        {
          ...input,
          sorts: [sortOverride],
          filters: [...input.filters, ...toolbarFilters],
          metricsStartDay: cloneDateWindow.startDay ?? undefined,
          metricsEndDay: cloneDateWindow.endDay ?? undefined,
          previousMetricsStartDay: cloneDateWindow.previousStartDay ?? undefined,
          previousMetricsEndDay: cloneDateWindow.previousEndDay ?? undefined,
          include: {
            project: true,
            latestExecution: true,
            executionStatusCounts: true,
            latestTestResults: { counts: true },
            metrics: true,
            metricDeltas: showCloneDeltas,
          },
        },
      );
    },
    [
      cloneDateWindow.endDay,
      cloneDateWindow.previousEndDay,
      cloneDateWindow.previousStartDay,
      cloneDateWindow.startDay,
      cloneFilterStatuses,
      cloneFilterTags,
      cloneSortDirection,
      cloneSortKey,
      showCloneDeltas,
      viewModel.selectedOrganisationId,
      viewModel.selectedProjectId,
    ],
  );
  const cloneExecutionQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<SuiteExecutionDto>> => {
      const isLatestQuickFilter =
        cloneDateQuickFilter === 'latest' && cloneFromDate === null && cloneToDate === null;
      const baseFilters = isLatestQuickFilter
        ? input.filters.filter(
            (filter) =>
              filter.field !== 'timestamp' &&
              filter.field !== 'createdAt' &&
              filter.field !== 'isLatest',
          )
        : input.filters;
      const toolbarFilters = mergeFilters(
        buildInFilter('status', cloneExecutionFilterStatuses),
        buildInFilter('tags', cloneExecutionFilterTags),
        isLatestQuickFilter
          ? buildEqFilterValue('isLatest', true)
          : buildDateRangeFilters(
              'timestamp',
              cloneDateWindow.startDay
                ? dayjs(cloneDateWindow.startDay).startOf('day').toISOString()
                : null,
              cloneDateWindow.endDay
                ? dayjs(cloneDateWindow.endDay).endOf('day').toISOString()
                : null,
            ),
      );
      const sortOverride: ApiSort = {
        field: cloneExecutionSortFieldMap[cloneExecutionSortKey],
        direction: cloneExecutionSortDirection,
      };
      return querySuiteExecutions(
        viewModel.selectedProjectId,
        viewModel.selectedOrganisationId ?? undefined,
        {
          ...input,
          search: debouncedCloneExecutionSearch,
          sorts: [sortOverride],
          filters: [...baseFilters, ...toolbarFilters],
        },
      );
    },
    [
      cloneDateWindow.endDay,
      cloneDateWindow.startDay,
      cloneDateQuickFilter,
      cloneFromDate,
      cloneToDate,
      cloneExecutionFilterStatuses,
      cloneExecutionFilterTags,
      cloneExecutionSortDirection,
      cloneExecutionSortKey,
      debouncedCloneExecutionSearch,
      viewModel.selectedOrganisationId,
      viewModel.selectedProjectId,
    ],
  );
  const cloneNoRowsText = cloneDateWindow.hasCustomBounds
    ? 'No suites found for selected date range'
    : formatRangeWindowNoDataText('No suites found', cloneDateQuickFilter, false);
  const cloneExecutionsNoRowsText = cloneDateWindow.hasCustomBounds
    ? 'No suite executions found for selected date range'
    : formatRangeWindowNoDataText('No suite executions found', cloneDateQuickFilter, false);
  const cloneStatusFilterKey = useMemo(
    () => [...cloneFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterStatuses],
  );
  const cloneTagFilterKey = useMemo(
    () => [...cloneFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterTags],
  );
  const cloneExecutionStatusFilterKey = useMemo(
    () =>
      [...cloneExecutionFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterStatuses],
  );
  const cloneExecutionTagFilterKey = useMemo(
    () => [...cloneExecutionFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterTags],
  );
  const cloneSuiteExecutionDateBounds = useMemo(
    () => resolveMetaDateBounds(cloneSuiteExecutionsMeta, (item) => item.timestamp),
    [cloneSuiteExecutionsMeta],
  );
  const cloneContentOverride = cloneTableEnabled ? (
    <Panel
      padding="xl"
      gap="none"
      contentGap="md"
      headerGap="none"
      headerSlot={
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap="md"
            sx={{
              minWidth: 0,
              minHeight: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
              height: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
            }}
          >
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
              <Tabs
                value={cloneActiveTab}
                onChange={(_event, next) => {
                  const nextTab = next as CloneSuitesPanelTab;
                  setCloneActiveTab(nextTab);
                  if (nextTab === 'suites' && cloneDateQuickFilter === 'latest') {
                    setCloneDateQuickFilter('7d');
                  }
                }}
                aria-label="Clone suites panel tabs"
              >
                <Tab label="Suites" value="suites" />
                <Tab label="Executions" value="executions" />
              </Tabs>
            </Box>
            {cloneActiveTab === 'suites' ? (
              <Box sx={{ flex: '0 0 auto' }}>
                <TableToolbar<CloneSortKey, SuiteExecutionStatus>
                  title={undefined}
                  searchAriaLabel="Search suites, tags"
                  onDebouncedSearchChange={handleDebouncedCloneSearchChange}
                  columnVisibilityModel={cloneColumnVisibilityModel}
                  defaultColumnVisibilityModel={cloneDefaultColumnVisibilityModel}
                  columnOptions={cloneColumnOptions}
                  onColumnVisibilityModelChange={setCloneColumnVisibilityModel}
                  sortKey={cloneSortKey}
                  sortDirection={cloneSortDirection}
                  defaultSortKey={cloneDefaultSortKey}
                  defaultSortDirection={cloneDefaultSortDirection}
                  sortOptions={cloneSortOptions}
                  onSortChange={(nextSortKey, nextSortDirection) => {
                    setCloneSortKey(nextSortKey);
                    setCloneSortDirection(nextSortDirection);
                  }}
                  filterStatuses={cloneFilterStatuses}
                  statusOptions={cloneStatusOptions}
                  onFilterStatusesChange={setCloneFilterStatuses}
                  filterTags={cloneFilterTags}
                  onFilterTagsChange={setCloneFilterTags}
                  dateQuickFilter={cloneDateQuickFilter}
                  onDateQuickFilterChange={(next) => {
                    setCloneDateQuickFilter(next);
                    setCloneFromDate(null);
                    setCloneToDate(null);
                  }}
                  fromDate={cloneFromDate}
                  toDate={cloneToDate}
                  onFromDateChange={setCloneFromDate}
                  onToDateChange={setCloneToDate}
                  availableTags={cloneAvailableTags}
                  summaryCount={cloneSuitesMeta?.total ?? null}
                  summarySingularLabel="suite"
                  summaryPluralLabel="suites"
                />
              </Box>
            ) : null}
            {cloneActiveTab === 'executions' ? (
              <Box sx={{ flex: '0 0 auto' }}>
                <TableToolbar<CloneExecutionSortKey, SuiteExecutionStatus>
                  title={undefined}
                  searchAriaLabel="Search suite executions"
                  onDebouncedSearchChange={handleDebouncedCloneExecutionSearchChange}
                  columnVisibilityModel={cloneExecutionColumnVisibilityModel}
                  defaultColumnVisibilityModel={cloneExecutionDefaultColumnVisibilityModel}
                  columnOptions={cloneExecutionColumnOptions}
                  onColumnVisibilityModelChange={setCloneExecutionColumnVisibilityModel}
                  sortKey={cloneExecutionSortKey}
                  sortDirection={cloneExecutionSortDirection}
                  defaultSortKey={cloneExecutionDefaultSortKey}
                  defaultSortDirection={cloneExecutionDefaultSortDirection}
                  sortOptions={cloneExecutionSortOptions}
                  onSortChange={(nextSortKey, nextSortDirection) => {
                    setCloneExecutionSortKey(nextSortKey);
                    setCloneExecutionSortDirection(nextSortDirection);
                  }}
                  filterStatuses={cloneExecutionFilterStatuses}
                  statusOptions={cloneExecutionStatusOptions}
                  onFilterStatusesChange={setCloneExecutionFilterStatuses}
                  filterTags={cloneExecutionFilterTags}
                  onFilterTagsChange={setCloneExecutionFilterTags}
                  dateQuickFilter={cloneDateQuickFilter}
                  onDateQuickFilterChange={(next) => {
                    setCloneDateQuickFilter(next);
                    setCloneFromDate(null);
                    setCloneToDate(null);
                  }}
                  fromDate={cloneFromDate}
                  toDate={cloneToDate}
                  onFromDateChange={setCloneFromDate}
                  onToDateChange={setCloneToDate}
                  availableTags={cloneExecutionAvailableTags}
                  includeLatestQuickFilter
                  statusFilterLabel="Status"
                  summaryCount={cloneSuiteExecutionsMeta?.total ?? null}
                  summarySingularLabel="suite execution"
                  summaryPluralLabel="suite executions"
                  previewFromDate={
                    cloneDateQuickFilter === 'latest' ? cloneSuiteExecutionDateBounds.from : null
                  }
                  previewToDate={
                    cloneDateQuickFilter === 'latest' ? cloneSuiteExecutionDateBounds.to : null
                  }
                />
              </Box>
            ) : null}
          </Stack>
        </Box>
      }
      sx={{ backgroundColor: 'background.paper' }}
    >
      {cloneActiveTab === 'suites' ? (
        <SuitesTable
          persistKey={null}
          queryKey={[
            'suites-table-clone',
            cloneDateQuickFilter,
            viewModel.selectedOrganisationId ?? null,
            viewModel.selectedProjectId ?? null,
            showCloneDeltas ? 'deltas-on' : 'deltas-off',
            cloneSortKey,
            cloneSortDirection,
            cloneStatusFilterKey,
            cloneTagFilterKey,
            cloneDateWindow.startDay,
            cloneDateWindow.endDay,
          ]}
          queryFn={cloneQueryFn}
          pageSizeOptions={[5, 10, 25, 50]}
          initialPageSize={10}
          showDeltas={showCloneDeltas}
          noRowsText={cloneNoRowsText}
          showToolbar={false}
          disableColumnSorting
          disableColumnMenu
          search={debouncedCloneSearch}
          onQueryMeta={setCloneSuitesMeta}
          sparklineRangeDays={cloneDateWindow.sparklineRangeDays}
          metricsStartDay={cloneDateWindow.startDay ?? undefined}
          metricsEndDay={cloneDateWindow.endDay ?? undefined}
          columnVisibilityModel={cloneColumnVisibilityModel}
          onColumnVisibilityModelChange={(nextModel) => {
            setCloneColumnVisibilityModel({
              ...nextModel,
              name: true,
            });
          }}
        />
      ) : null}
      {cloneActiveTab === 'executions' ? (
        <SuiteExecutionsTable
          persistKey={null}
          queryKey={[
            'suite-executions-table-clone',
            cloneDateQuickFilter,
            viewModel.selectedOrganisationId ?? null,
            viewModel.selectedProjectId ?? null,
            cloneExecutionSortKey,
            cloneExecutionSortDirection,
            cloneExecutionStatusFilterKey,
            cloneExecutionTagFilterKey,
            debouncedCloneExecutionSearch,
            cloneDateWindow.startDay,
            cloneDateWindow.endDay,
          ]}
          queryFn={cloneExecutionQueryFn}
          pageSizeOptions={[5, 10, 25, 50]}
          initialPageSize={10}
          noRowsText={cloneExecutionsNoRowsText}
          showToolbar={false}
          disableColumnSorting
          disableColumnMenu
          search={debouncedCloneExecutionSearch}
          onQueryMeta={setCloneSuiteExecutionsMeta}
          columnVisibilityModel={cloneExecutionColumnVisibilityModel}
          onColumnVisibilityModelChange={(nextModel) => {
            setCloneExecutionColumnVisibilityModel({
              ...nextModel,
              name: true,
            });
          }}
        />
      ) : null}
    </Panel>
  ) : undefined;

  return (
    <Stack gap="md">
      <EntityTrendsPageShell
        viewModel={viewModel}
        title="Suites"
        addButtonLabel="Add Suite"
        searchAriaLabel="Search suites"
        chartTabsAriaLabel="Suites chart tabs"
        pageTabsAriaLabel="Suites page tabs"
        entityTabLabel="Suites"
        chartEmptyStateTitle="No suite executions found"
        entityTableNoRowsText="No suites found"
        executionTableNoRowsText="No suite executions found"
        persistKey="suites-table"
        executionPersistKey="suite-executions-table"
        contentOverride={cloneContentOverride}
        renderEntityTable={({
          queryKey,
          queryFn,
          fixedFilters,
          showDeltas,
          persistKey,
          noRowsText,
        }) => (
          <SuitesTable
            persistKey={persistKey}
            queryKey={queryKey}
            queryFn={queryFn}
            pageSizeOptions={[10]}
            initialPageSize={10}
            disablePageSizeSelect
            showDeltas={showDeltas}
            fixedFilters={fixedFilters}
            noRowsText={noRowsText}
          />
        )}
        renderExecutionsTable={({ fixedFilters, persistKey, noRowsText }) => (
          <SuiteExecutionsTable
            persistKey={persistKey}
            fixedFilters={fixedFilters}
            pageSizeOptions={[10]}
            initialPageSize={10}
            disablePageSizeSelect
            noRowsText={noRowsText}
          />
        )}
      />
    </Stack>
  );
};
